import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Reset key — when this changes (e.g. route key), the boundary clears its
   *  error state so navigating away from a broken page recovers. */
  resetKey?: string;
}

interface State {
  err: Error | null;
}

/**
 * Catches render-time crashes inside a route subtree. Shows a friendly card
 * with the error message + a "try again" reload, instead of unmounting the
 * whole app to a blank page. Doesn't catch async errors (TanStack Query
 * surfaces those via `isError`).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.err && prev.resetKey !== this.props.resetKey) {
      this.setState({ err: null });
    }
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // Console is the only telemetry sink we have client-side. Sentry-style
    // capture can plug in here later if needed.
    console.error('[ErrorBoundary]', err, info.componentStack);
  }

  render() {
    if (!this.state.err) return this.props.children;

    return (
      <div className="p-6">
        <div className="card max-w-xl border border-kam-red/30 bg-kam-red-soft">
          <div className="flex items-start gap-3 mb-3">
            <AlertOctagon size={20} className="text-kam-red flex-none mt-0.5" />
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-kam-red mb-1">Something broke on this page.</h2>
              <p className="text-xs text-text-dim">
                The rest of the dashboard is unaffected — switch pages and the error stays here.
              </p>
            </div>
          </div>
          <pre className="text-xs font-mono text-text-dim bg-bg/40 border border-[color:var(--border)] rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all">
            {this.state.err.message}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-surface-2 text-text border border-[color:var(--border)] hover:border-kam-violet/40 hover:text-kam-violet"
          >
            <RefreshCw size={12} />
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
